import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { catchError, map, Observable, of } from 'rxjs';

import { LoggerService } from '../logger/logger.service';

export interface AccountStatusResponse {
  redirectUrl: string;
  status: string;
}

export interface SessionExpiryDetails {
  sessionExpiryTime: number;
  isFinalSessionExtensionOver: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserSessionApiService {
  private http = inject(HttpClient);
  private logger = inject(LoggerService).setContext('UserSessionApiService');
  private BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;

  verifyAccountStatus(returnUrl?: string): Observable<AccountStatusResponse | null> {
    let urlToReturn = returnUrl || environment.CRG_HOME;

    // Remove trailing slash for consistency
    if (urlToReturn.endsWith('/')) {
      urlToReturn = urlToReturn.slice(0, -1);
    }

    const url = `${this.BASE_URL}/v1/heartbeat/verifyAccountStatus?isOIDC=true&returnUrl=${encodeURIComponent(urlToReturn)}`;

    return this.http
      .post<AccountStatusResponse>(url, null, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/javascript, */*; q=0.01'
        },
        withCredentials: true,
        observe: 'response' // Get full response including status code
      })
      .pipe(
        map((response) => response.body),
        catchError((error) => {
          if (error.status === 401) {
            this.logger.warn('User not authenticated (401)');
          } else {
            this.logger.error('Error verifying account status:', error);
          }

          return of(null);
        })
      );
  }

  getSessionExpiryDetails(): Observable<SessionExpiryDetails | null> {
    const url = `${this.BASE_URL}/v1/sessionexpirydetails`;

    return this.http
      .get<SessionExpiryDetails>(url, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/javascript, */*; q=0.01'
        },
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map((response) => response.body),
        catchError((error) => {
          if (error.status === 401) {
            this.logger.warn('Session expired (401)');
          } else {
            this.logger.error('Error getting session expiry:', error);
          }

          return of(null);
        })
      );
  }

  extendSession(): Observable<boolean> {
    const url = `${this.BASE_URL}/api/v1/session/extend`;

    return this.http
      .post(url, null, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/javascript, */*; q=0.01'
        },
        withCredentials: true
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          this.logger.error('Error extending session:', error);
          return of(false);
        })
      );
  }

  logout(returnUrl: string): void {
    // Remove trailing slash for consistency
    let urlToReturn = returnUrl;
    if (urlToReturn.endsWith('/')) {
      urlToReturn = urlToReturn.slice(0, -1);
    }

    const url = `${this.BASE_URL}/v1/oidc/logout?returnUrl=${encodeURIComponent(urlToReturn)}`;
    window.location.href = url;
  }
}
